import * as _ from 'lodash';
import * as angular from 'angular';

import {
    getSmartHomeStarterKitIsActivated,
    hasSmartHomeStarterKit,
    selectedWindMitigationDiscount
} from 'common/source/utilities/policyInfoHelper';

// the below functions wrap _ to restrict the input types to indicate accidentally incorrect use

function anyIsDefined(...valueArray: Array<any>) {
    return _.some(valueArray, function(value) {
        return value !== undefined;
    });
}

function isBoolean(value: boolean | undefined | null) {
    return _.isBoolean(value);
}

function isString(value: string | undefined | null) {
    return _.isString(value);
}

function isNotFalsyOrStringNo(value: any) {
    return value && value !== 'No';
}

export class DiscountsHelper {
    private policyInfo: CustomerPolicyInfo;
    private propertyData: PropertyData;

    constructor(policyInfo: CustomerPolicyInfo) {
        this.policyInfo = policyInfo;
        this.propertyData = policyInfo.property_data;
    }

    // use this to say whether they should get a discount
    // we don't usually look at the rater data.... we don't define it as a discount,
    // we define it in the rater and don't expose that
    // we don't store the choices we made inside the rater, we just store the end result of the rater

    // earthquake retrofit discount: already wrote a thing where if in a zipcode where it doesn't matter,
    // we don't ask you.. and we shouldn't tell you it's a discount...
    // (like Hagai said then it wouldn't even be a field)

    hasMultiplePoliciesDiscount(): boolean | undefined {
        // "in applicable states"
        // we only call this auto in California...
        // usually would be if you have auto and home with the same company
        // but we decided to give it to anyone in California who has any auto policy
        return this.policyInfo.personal_information.multiple_policies_discount;
    }

    receivedMultiplePoliciesDiscount(): boolean {
        // "in applicable states"
        // we only call this auto in California...
        // usually would be if you have auto and home with the same company
        // but we decided to give it to anyone in California who has any auto policy
        return !!this.hasMultiplePoliciesDiscount();
    }

    wasOfferedMultiplePoliciesDiscount(): boolean {
        const state = this.propertyData.address.state;

        if (state.toLowerCase() === 'tx') {
            return false; // patching a pod bug
        }

        return isBoolean(this.hasMultiplePoliciesDiscount());
    }

    // we might need to store more data from the rater externally
    // property_data.rater is already a thing...
    // it may make sense to put result of early quote discount on the rater as well
    // not trivial to do right now...
    // we convert the policyInfo into params, we get a result that is just a number
    // so we may already want to make the premium calculation return more metadata, not just the number
    // and we can then put that on the rater

    receivedEarlyBirdDiscount() {
        return (
            !!this.policyInfo.calculated_fields.full_early_quote_discount ||
            !!this.policyInfo.calculated_fields.partial_early_quote_discount
        );
    }

    wasOfferedEarlyBirdDiscount() {
        return (
            isBoolean(this.policyInfo.calculated_fields.full_early_quote_discount) ||
            isBoolean(this.policyInfo.calculated_fields.partial_early_quote_discount)
        );
    }

    wasOfferedCosmeticDamageExclusionDiscount() {
        return (
            isBoolean(this.policyInfo.property_data.cosmetic_damage_exclusion) &&
            this.receivedCosmeticDamageExclusionDiscount()
        );
    }

    receivedCosmeticDamageExclusionDiscount() {
        return !!this.policyInfo.property_data.cosmetic_damage_exclusion;
    }

    wasRecentHomePurchase() {
        // next_3_months || last_12_months
        return this.propertyData.purchase_date !== 'over_1_year_ago';
    }

    receivedRecentHomePurchaseDiscount() {
        return this.wasRecentHomePurchase();
    }

    wasOfferedRecentHomePurchaseDiscount() {
        return isString(this.propertyData.purchase_date);
    }

    isInGatedCommunity() {
        // this.propertyData.guard_self_enclosed is an old version we stopped using
        // all values in the database as of 05/21/2018 are false
        return this.propertyData.guard;
    }

    receivedGatedCommunityDiscount() {
        return !!this.isInGatedCommunity();
    }

    wasOfferedGatedCommunityDiscount() {
        const state = this.propertyData.address.state;

        if (state.toLowerCase() === 'tx') {
            return false; // patching a pod bug
        }

        return isBoolean(this.isInGatedCommunity());
    }

    hasHomeOwnersAssociation() {
        return this.propertyData.hoa_membership;
    }

    receivedHomeOwnersAssociationDiscount() {
        return !!this.hasHomeOwnersAssociation();
    }

    wasOfferedHomeOwnersAssociationDiscount() {
        return isBoolean(this.hasHomeOwnersAssociation());
    }

    hasNoMortgageDiscount() {
        return this.propertyData.no_mortgage_discount;
    }

    receivedNoMortgageDiscount() {
        return !!this.hasNoMortgageDiscount();
    }

    wasOfferedNoMortgageDiscount() {
        return isBoolean(this.hasNoMortgageDiscount());
    }

    hasStormShutters() {
        return this.propertyData.other_wind_loss_prevention === 'shutters';
    }

    receivedStormShuttersDiscount() {
        return !!this.hasStormShutters();
    }

    wasOfferedStormShuttersDiscount() {
        return isString(this.propertyData.other_wind_loss_prevention);
    }

    hasWindCoverageExclusion() {
        return this.policyInfo.calculated_fields.wind_exclusion;
    }

    receivedWindCoverageExclusionDiscount() {
        return !!this.hasWindCoverageExclusion();
    }

    wasOfferedWindCoverageExclusionDiscount() {
        return isBoolean(this.hasWindCoverageExclusion());
    }

    hasWindResistantHomeDiscount() {
        return selectedWindMitigationDiscount(this.policyInfo);
    }

    receivedWindResistantHomeDiscount() {
        return !!this.hasWindResistantHomeDiscount();
    }

    wasOfferedWindResistantHomeDiscount() {
        return isString(this.propertyData.wind_mitigation);
    }

    hasTheftAlarm() {
        // theft alarm (either if was asked under the smart home question or not) -
        // “A theft alarm which is monitored by a security company or a police station”
        return (
            this.propertyData.local_burglar_alarm ||
            this.propertyData.central_burglar_alarm ||
            isNotFalsyOrStringNo(this.propertyData.smart_home_burglar_alarm)
        );
    }

    receivedTheftAlarmDiscount() {
        return !!this.hasTheftAlarm();
    }

    wasOfferedTheftAlarmDiscount() {
        return anyIsDefined(
            this.propertyData.local_burglar_alarm,
            this.propertyData.central_burglar_alarm,
            this.propertyData.smart_home_burglar_alarm
        );
    }

    hasFireAlarm() {
        return (
            this.propertyData.local_fire_alarm ||
            this.propertyData.central_fire_alarm ||
            isNotFalsyOrStringNo(this.propertyData.smart_home_fire_alarm)
        );
    }

    receivedFireAlarmDiscount() {
        return !!this.hasFireAlarm();
    }

    wasOfferedFireAlarmDiscount() {
        // is this true?
        return anyIsDefined(
            this.propertyData.local_fire_alarm,
            this.propertyData.central_fire_alarm,
            this.propertyData.smart_home_fire_alarm
        );
    }

    hasWaterLeakSensorOrValveClosureDevices() {
        return (
            this.propertyData.water_leak_device || isNotFalsyOrStringNo(this.propertyData.smart_home_water_leak_device)
        );
    }

    receivedWaterLeakSensorOrValveClosureDevices() {
        return !!this.hasWaterLeakSensorOrValveClosureDevices();
    }

    wasOfferedWaterLeakSensorOrValveClosureDevices() {
        return anyIsDefined(this.propertyData.smart_home_water_leak_device, this.propertyData.water_leak_device);
    }

    hasHailResistantRoof() {
        return this.propertyData.hail_resistant_roof;
    }

    receivedHailResistantRoofDiscount() {
        return !!this.hasHailResistantRoof();
    }

    wasOfferedHailResistantRoofDiscount() {
        return isBoolean(this.hasHailResistantRoof());
    }

    hasFireExtinguisher() {
        return this.propertyData.fire_ext;
    }

    receivedFireExtinguisherDiscount() {
        return !!this.hasFireExtinguisher();
    }

    wasOfferedFireExtinguisherDiscount() {
        return isBoolean(this.hasFireExtinguisher());
    }

    hasSprinklerSystem() {
        return this.propertyData.sprinkler;
    }

    receivedSprinklerSystemDiscount() {
        return !!this.hasSprinklerSystem();
    }

    wasOfferedSprinklerSystemDiscount() {
        return isBoolean(this.hasSprinklerSystem());
    }

    receivedSmartHomeStarterKitDiscount() {
        return hasSmartHomeStarterKit(this.policyInfo) && getSmartHomeStarterKitIsActivated(this.policyInfo);
    }

    wasOfferedComcastAffinityDiscount() {
        return isBoolean(this.propertyData.preferred_affinity_discount);
    }

    receivedComcastAffinityDiscount() {
        return !!this.propertyData.preferred_affinity_discount;
    }
}

// the following is to make this code accessible to unit tests

export const module = angular.module('customerPortal.utilities.discountsHelper.DiscountsHelper', []);

module.factory('DiscountsHelper', function() {
    return DiscountsHelper;
});
